import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { chatApi, authApi } from '../services/api';
import { resolveMentorFromBio } from '../lib/voiceProfile';
import {
  cancelSpeechPlayback,
  prepareSpeechAudio,
  resetAudioPlaybackGate,
  SPOKEN_TEXT_ATTRS,
} from '../lib/speechPlayback';
import './LivingRoom.css';

const LOGO = encodeURI('/Assets/logo/Mentology Logo.png');
const DEFAULT_MENTOR_VOICE_ID = 's116Gqp9aiAQAe29Albd';

interface Message {
  role: string;
  content: string;
}

interface Bio {
  preferred_name?: string;
  name?: string;
  language_target?: string;
  ai_mentor_gender?: string;
  ai_mentor_name?: string;
  ai_mentor_voice_id?: string;
}

export const PRIVATE_REFLECTION_HOOKS: string[] = [];
export const CORPORATE_REFLECTION_HOOKS: string[] = [];

export const DELETE_FILES_CONFIRM_MESSAGE =
  'CRITICAL WARNING: This system setup cannot be restored and all files will be permanently deleted. Do you want to proceed?';

interface LivingRoomConsoleProps {
  screenId: string;
  welcomeScript: string;
  reflectionHooks?: string[];
  menuContent: (closeMenu: () => void) => ReactNode;
  qrModal?: ReactNode;
  documentUploadModal?: ReactNode;
  assetBlocks?: ReactNode;
}

function resolveMenteeName(bio: Bio | null, userName?: string) {
  if (bio?.preferred_name?.trim()) return bio.preferred_name.trim();
  if (bio?.name?.trim()) return bio.name.trim().split(/\s+/)[0];
  if (userName?.trim()) return userName.trim().split(/\s+/)[0];
  return 'there';
}

function personalizeWelcomeScript(script: string, bio: Bio | null) {
  const name = resolveMenteeName(bio);
  return script.replace(/\[Mentee(?:'s)? Name\]/gi, name);
}

function estimateSpeechDurationMs(script: string) {
  const words = script.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(3000, (words / 150) * 60 * 1000);
}

export default function LivingRoomConsole({
  screenId,
  welcomeScript,
  reflectionHooks = [],
  menuContent,
  qrModal,
  documentUploadModal,
  assetBlocks,
}: LivingRoomConsoleProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<HTMLDivElement>(null);
  const introAudioRef = useRef<HTMLAudioElement | null>(null);
  const syncRafRef = useRef<number | null>(null);
  const syncStartRef = useRef(0);
  const syncDurationRef = useRef(0);
  const playbackStarted = useRef(false);
  const recognitionRef = useRef<{ stop: () => void; start: () => void } | null>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [displayWelcome, setDisplayWelcome] = useState('');
  const [introComplete, setIntroComplete] = useState(false);
  const [consoleFrozen, setConsoleFrozen] = useState(false);
  const [freezeMessage, setFreezeMessage] = useState('');

  const stopSyncAnimation = useCallback(() => {
    if (syncRafRef.current != null) {
      cancelAnimationFrame(syncRafRef.current);
      syncRafRef.current = null;
    }
  }, []);

  const finishIntro = useCallback((script: string) => {
    stopSyncAnimation();
    setDisplayWelcome(script);
    setIntroComplete(true);
  }, [stopSyncAnimation]);

  const runAudioSyncedTypewriter = useCallback((script: string, audio: HTMLAudioElement) => {
    const durationMs = audio.duration && Number.isFinite(audio.duration) && audio.duration > 0
      ? audio.duration * 1000
      : estimateSpeechDurationMs(script);

    syncDurationRef.current = durationMs;
    syncStartRef.current = performance.now();

    const tick = () => {
      const elapsed = performance.now() - syncStartRef.current;
      const progress = Math.min(1, elapsed / syncDurationRef.current);
      const charCount = Math.max(1, Math.ceil(progress * script.length));
      setDisplayWelcome(script.slice(0, charCount));

      if (progress < 1 && !audio.ended) {
        syncRafRef.current = requestAnimationFrame(tick);
        return;
      }

      finishIntro(script);
    };

    syncRafRef.current = requestAnimationFrame(tick);
  }, [finishIntro]);

  const startSyncedIntro = useCallback(async (script: string, bio: Bio | null) => {
    if (playbackStarted.current) return;
    playbackStarted.current = true;

    const mentorProfile = resolveMentorFromBio(bio);
    const voiceId = mentorProfile.voiceId || DEFAULT_MENTOR_VOICE_ID;
    const languageTarget = bio?.language_target ?? undefined;

    setDisplayWelcome(script.charAt(0) || '');
    setIntroComplete(false);
    resetAudioPlaybackGate();
    stopSyncAnimation();

    try {
      const audio = await prepareSpeechAudio(voiceId, script, languageTarget);
      introAudioRef.current = audio;

      await audio.play();
      runAudioSyncedTypewriter(script, audio);

      audio.onended = () => {
        finishIntro(script);
      };
    } catch (error) {
      console.warn('[Briefing sync fallback]', error);
      setDisplayWelcome(script);
      setIntroComplete(true);
    }
  }, [finishIntro, runAudioSyncedTypewriter, stopSyncAnimation]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      stopSyncAnimation();
      if (introAudioRef.current) {
        introAudioRef.current.pause();
        introAudioRef.current = null;
      }
      cancelSpeechPlayback();
      playbackStarted.current = false;
    };
  }, [stopSyncAnimation]);

  useEffect(() => {
    let cancelled = false;
    const pollFreeze = () => {
      chatApi.consoleState()
        .then((state) => {
          if (cancelled) return;
          if (state.pendingThaw) {
            setConsoleFrozen(false);
            setFreezeMessage('');
            return;
          }
          setConsoleFrozen(!!state.frozen);
          setFreezeMessage(state.systemMessage || '');
        })
        .catch(() => {});
    };
    pollFreeze();
    const interval = setInterval(pollFreeze, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  useEffect(() => {
    let cancelled = false;
    playbackStarted.current = false;
    stopSyncAnimation();
    cancelSpeechPlayback();

    Promise.all([
      authApi.getBio().catch(() => ({ bio: null as Bio | null })),
      chatApi.getHistory().catch(() => ({ messages: [] as Message[] })),
    ]).then(([bioData, historyData]) => {
      if (cancelled) return;

      const script = personalizeWelcomeScript(welcomeScript, bioData.bio);
      const history = historyData.messages || [];

      if (history.length > 0) {
        setMessages(history);
        setDisplayWelcome(script);
        setIntroComplete(true);
        playbackStarted.current = true;
        return;
      }

      void startSyncedIntro(script, bioData.bio);
    });

    return () => {
      cancelled = true;
    };
  }, [welcomeScript, startSyncedIntro, stopSyncAnimation]);

  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [displayWelcome, messages, loading, introComplete]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages((current) => [...current, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const result = await chatApi.sendMessage(userMsg);
      if (result.crisis) {
        setConsoleFrozen(true);
        setFreezeMessage(result.message || result.injectedMessage || '');
        setMessages((current) => [...current, { role: 'system', content: result.message }]);
        return;
      }
      setMessages((current) => [
        ...current,
        { role: 'assistant', content: result.message.content },
      ]);
    } catch (err: unknown) {
      const e = err as { error?: string };
      setMessages((current) => [
        ...current,
        { role: 'system', content: e.error || 'Message failed' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const toggleMic = () => {
    const win = window as Window & {
      SpeechRecognition?: new () => SpeechRecognitionInstance;
      webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
    };
    type SpeechRecognitionInstance = {
      continuous: boolean;
      interimResults: boolean;
      lang: string;
      start: () => void;
      stop: () => void;
      onresult: ((event: { results: ArrayLike<{ 0: { transcript: string } }> }) => void) | null;
      onend: (() => void) | null;
      onerror: (() => void) | null;
    };

    const SpeechRecognitionCtor = win.SpeechRecognition || win.webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) {
      alert('Voice input is not supported in this browser.');
      return;
    }

    if (listening && recognitionRef.current) {
      recognitionRef.current.stop();
      setListening(false);
      return;
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-AU';
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join('');
      setInput(transcript);
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const applyReflectionHook = (prompt: string) => {
    setInput((current) => (current.trim() ? `${current.trim()} ${prompt}` : prompt));
  };

  return (
    <div className="living-room-screen" data-screen-id={screenId}>
      <header className="living-room-top-bar">
        <div className="living-room-brand">
          <img
            src={LOGO}
            alt="Mentology"
            className="living-room-logo brand-logo"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <h1 className="living-room-title">Briefing Room</h1>
        </div>
        <div className="living-room-util" ref={menuRef}>
          <button
            type="button"
            className="living-room-util-trigger"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
          >
            Menu ▾
          </button>
          {menuOpen && (
            <div className="living-room-util-dropdown">
              {menuContent(() => setMenuOpen(false))}
            </div>
          )}
        </div>
      </header>

      <div className="living-room-body">
        {assetBlocks}
        <div
          ref={streamRef}
          className={`living-room-stream ${SPOKEN_TEXT_ATTRS.className}`}
        >
          <div className={`living-room-welcome scannable-bullet-text ${SPOKEN_TEXT_ATTRS.className}`}>
            {displayWelcome}
            {!introComplete && <span className="living-room-cursor">|</span>}
          </div>
          <div className="living-room-thread">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`living-room-bubble living-room-bubble-${message.role} scannable-bullet-text ${SPOKEN_TEXT_ATTRS.className}`}
              >
                {message.content}
              </div>
            ))}
            {loading && (
              <p className={`living-room-bubble scannable-bullet-text ${SPOKEN_TEXT_ATTRS.className}`}>
                Processing...
              </p>
            )}
          </div>
        </div>

        {reflectionHooks.length > 0 && (
          <div className="living-room-reflection-hooks">
            {reflectionHooks.map((hook) => (
              <button
                key={hook}
                type="button"
                className="living-room-reflection-hook"
                onClick={() => applyReflectionHook(hook)}
              >
                {hook}
              </button>
            ))}
          </div>
        )}
      </div>

      {consoleFrozen && (
        <div className="living-room-freeze-overlay" role="alert">
          <p className="living-room-freeze-title">Console Frozen — Red Phone Protocol Active</p>
          <p className="living-room-freeze-message">{freezeMessage}</p>
        </div>
      )}

      <form
        className="living-room-input-wrap"
        onSubmit={(event) => {
          event.preventDefault();
          if (input.trim() && !loading && !consoleFrozen) sendMessage();
        }}
      >
        <textarea
          className="living-room-chat-input"
          placeholder={consoleFrozen ? 'Console frozen pending mentor review...' : 'Share your challenge, reflection, or response...'}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              if (input.trim() && !loading && !consoleFrozen) sendMessage();
            }
          }}
          disabled={loading || consoleFrozen}
          aria-label="Mentee message input"
        />
        <div className="living-room-input-controls">
          <button
            type="submit"
            className="living-room-send-btn"
            disabled={loading || !input.trim() || consoleFrozen}
          >
            Send
          </button>
          <button
            type="button"
            className={`living-room-mic-btn ${listening ? 'living-room-mic-active' : ''}`}
            onClick={toggleMic}
            disabled={loading || consoleFrozen}
            aria-label="Microphone"
            title="Microphone"
          >
            🎙️
          </button>
        </div>
      </form>

      {qrModal}
      {documentUploadModal}
    </div>
  );
}
